<template>
  <div class="cp-box" :class="{ 'cp-box--collapsed': collapsed }">
    <div class="cp-box__head">
      <md-button class="md-icon-button cp-box__collapse" :title="(collapsed ? (L.expand || 'Expand') : (L.collapse || 'Collapse'))" @click="collapsed = !collapsed">
        <md-icon>{{ collapsed ? 'chevron_right' : 'expand_more' }}</md-icon>
      </md-button>
      <span class="cp-box__num">{{ index + 1 }}</span>

      <!-- Title: text + pencil to edit; click pencil → inline input. The name is
           validated on commit (enter/blur): blank or duplicate is rejected and the
           field stays in edit mode with an inline error. -->
      <template v-if="editingName">
        <input
            ref="nameInput"
            class="cp-box__name-input"
            :class="{ 'cp-box__name-input--err': nameError }"
            v-model="nameDraft"
            :placeholder="L.box || 'Box'"
            @keydown.enter.prevent="commitName"
            @blur="commitName" />
        <span v-if="nameError" class="cp-box__name-err">{{ nameError }}</span>
      </template>
      <template v-else>
        <span class="cp-box__title" @dblclick="startRename">{{ box.name || ((L.box || 'Box') + ' ' + (index + 1)) }}</span>
        <md-button class="md-icon-button cp-box__rename" :title="L.renameBox || 'Rename'" @click="startRename"><md-icon>edit</md-icon></md-button>
      </template>

      <div class="cp-box__actions">
        <md-button v-if="index > 0" class="md-icon-button" :title="L.movePrev || 'Move back'" @click="$emit('move', index, -1)"><md-icon>{{ prevIcon }}</md-icon></md-button>
        <md-button v-if="index < total - 1" class="md-icon-button" :title="L.moveNext || 'Move forward'" @click="$emit('move', index, 1)"><md-icon>{{ nextIcon }}</md-icon></md-button>
        <md-button class="md-icon-button cp-box__delete" :title="L.removeBox || 'Remove box'" @click="$emit('remove', index)"><md-icon>delete</md-icon></md-button>
      </div>
    </div>

    <p v-show="!collapsed && !box.elements.length" class="cp-empty">{{ L.boxEmpty || 'Empty box — add an element below.' }}</p>

    <!-- Ordered elements, stacked and centered (not full width). -->
    <div v-show="!collapsed" class="cp-el-list">
    <div
        v-for="(el, ei) in box.elements"
        :key="el.uid || ei"
        class="cp-el">
      <div class="cp-el__bar">
        <md-icon class="cp-el__icon">{{ elementIcon(el.type) }}</md-icon>
        <span class="cp-el__type">{{ elementTypeLabel(el.type) }}</span>
        <!-- Main-label marker: this element supplies the label for the box's summary
             line. One per box — marking one clears the others. -->
        <md-button
            v-if="canBeMainLabel(el.type)"
            class="md-icon-button cp-el__main"
            :class="{ 'cp-el__main--on': el.mainLabel }"
            :title="el.mainLabel ? (L.mainLabelOn || 'Main label for the summary') : (L.mainLabelSet || 'Use as main label for the summary')"
            @click="toggleMainLabel(el)">
          <md-icon>{{ el.mainLabel ? 'star' : 'star_border' }}</md-icon>
        </md-button>
        <div class="cp-el__actions">
          <md-button v-if="ei > 0" class="md-icon-button" :title="L.moveUp || 'Move up'" @click="$emit('move-element', box, ei, -1)"><md-icon>arrow_upward</md-icon></md-button>
          <md-button v-if="ei < box.elements.length - 1" class="md-icon-button" :title="L.moveDown || 'Move down'" @click="$emit('move-element', box, ei, 1)"><md-icon>arrow_downward</md-icon></md-button>
          <md-button class="md-icon-button cp-el__delete" :title="L.remove || 'Remove'" @click="$emit('remove-element', box, ei)"><md-icon>close</md-icon></md-button>
        </div>
      </div>

      <component
          :is="elementComponent(el.type)"
          :options="options"
          :element="el"
          :lang="lang"
          @change-type="t => $emit('change-element-type', el, t)"
          @add-option="$emit('add-option', el)"
          @remove-option="i => $emit('remove-option', el, i)" />

      <!-- Connect this element to a sibling answer (show only when…). Shown when
           there's at least one eligible trigger element in the box. -->
      <ElementCondition
          v-if="hasTriggers(el)"
          :options="options"
          :element="el"
          :boxElements="box.elements"
          :lang="lang" />
    </div>
    </div>

    <!-- Add-element: round (+) button in a footer row, bottom-right of the box. -->
    <div v-show="!collapsed" class="cp-box__footer">
    <md-menu md-direction="top-end" md-size="small">
      <md-button class="cp-box__fab" md-menu-trigger :title="L.addElement || 'Add element'">
        <md-icon>add</md-icon>
      </md-button>
      <md-menu-content>
        <md-menu-item v-for="opt in addOptions" :key="opt.type" @click="$emit('add-element', box, opt.type)">
          <md-icon>{{ opt.icon }}</md-icon>
          <span>{{ opt.label }}</span>
        </md-menu-item>
      </md-menu-content>
    </md-menu>
    </div>
  </div>
</template>

<script>
import ElTitle from './elements/ElTitle.vue'
import ElTextarea from './elements/ElTextarea.vue'
import ElInput from './elements/ElInput.vue'
import ElChoices from './elements/ElChoices.vue'
import ElCheckbox from './elements/ElCheckbox.vue'
import ElementCondition from './ElementCondition.vue'

const ICONS = {
  title: 'title', textarea: 'notes', input: 'edit',
  single: 'radio_button_checked', multiple: 'check_box', checkbox: 'check_box_outline_blank'
};

/**
 * One composable box: an ordered list of elements the admin builds freely. Renders
 * the right element editor per element and an "add element" menu. All array-level ops
 * (add/remove/move element, options, type switch) are emitted to the parent editor.
 */
export default {
  name: 'BoxCard',
  components: { ElTitle, ElTextarea, ElInput, ElChoices, ElCheckbox, ElementCondition },
  props: {
    options: { type: Object, required: true },
    box: { type: Object, required: true },
    index: { type: Number, required: true },
    total: { type: Number, required: true },
    siblingNames: { type: Array, default: () => [] },   // other boxes' names, lowercased
    lang: { type: String, default: 'de' }
  },
  data() {
    return { collapsed: false, editingName: false, nameDraft: '', nameError: '' };
  },
  computed: {
    L() { return this.options.Lang || {}; },
    // Boxes flow in a 2-column grid, so the reorder arrows point at the box's real
    // neighbour: previous = up (from right col: left), next = right (from right col: down).
    isLeftColumn() { return this.index % 2 === 0; },
    prevIcon() { return this.isLeftColumn ? 'arrow_upward' : 'chevron_left'; },
    nextIcon() { return this.isLeftColumn ? 'chevron_right' : 'arrow_downward'; },
    addOptions() {
      const L = this.L;
      return [
        { type: 'title', icon: ICONS.title, label: L.elType_title || 'Title' },
        { type: 'textarea', icon: ICONS.textarea, label: L.elType_textarea || 'Text area' },
        { type: 'input', icon: ICONS.input, label: L.elType_input || 'Input (text / money)' },
        { type: 'single', icon: ICONS.single, label: L.elType_single || 'Single choice' },
        { type: 'multiple', icon: ICONS.multiple, label: L.elType_multiple || 'Multiple choice' },
        { type: 'checkbox', icon: ICONS.checkbox, label: L.elType_checkbox || 'Single checkbox' }
      ];
    }
  },
  methods: {
    // True if the box has any element (other than `el`) that can be a condition trigger.
    hasTriggers(el) {
      return (this.box.elements || []).some(e => e !== el && ['checkbox', 'single', 'multiple'].includes(e.type));
    },
    startRename() {
      this.nameDraft = this.box.name || '';
      this.nameError = '';
      this.editingName = true;
      this.$nextTick(() => { if (this.$refs.nameInput) { this.$refs.nameInput.focus(); this.$refs.nameInput.select(); } });
    },
    /** Commit the name. Blank is allowed (falls back to the default "Box N" display).
     *  A non-blank name must be unique vs the other boxes; otherwise stay editing. */
    commitName() {
      if (!this.editingName) return;   // guard double-fire (enter then blur)
      const name = (this.nameDraft || '').trim();
      if (name && this.siblingNames.includes(name.toLowerCase())) {
        this.nameError = this.L.boxNameDuplicate || 'Name already used.';
        this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus());
        return;
      }
      this.box.name = name;   // '' → default display
      this.nameError = '';
      this.editingName = false;
    },
    // Textarea (rich HTML) makes a poor summary label; everything else can supply one.
    canBeMainLabel(type) { return type !== 'textarea'; },
    /** Mark this element as the box's main label, clearing any other in the box. */
    toggleMainLabel(el) {
      const turnOn = !el.mainLabel;
      this.box.elements.forEach(e => { if (e !== el) this.$set(e, 'mainLabel', false); });
      this.$set(el, 'mainLabel', turnOn);
    },
    elementComponent(type) {
      if (type === 'title') return 'ElTitle';
      if (type === 'textarea') return 'ElTextarea';
      if (type === 'input') return 'ElInput';
      if (type === 'checkbox') return 'ElCheckbox';
      return 'ElChoices'; // single | multiple
    },
    elementIcon(type) { return ICONS[type] || 'crop_square'; },
    elementTypeLabel(type) {
      const opt = this.addOptions.find(o => o.type === type);
      return opt ? opt.label : type;
    }
  }
};
</script>

<style scoped>
/* Fixed-height box: head + scrollable element list + add button. Equal height across
   a row comes from the grid's align-items:stretch + this fixed height. */
.cp-box { position: relative; display: flex; flex-direction: column; height: 860px; border: 1px solid #e3e6ea; border-radius: 14px; padding: 1rem 1.25rem 1.25rem; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.04); box-sizing: border-box; }
/* Collapsed: shrink to just the header. */
.cp-box--collapsed { height: auto; }

.cp-box__head { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.5rem; flex-shrink: 0; }
.cp-box--collapsed .cp-box__head { margin-bottom: 0; }
.cp-box__collapse { margin: 0; flex-shrink: 0; }
.cp-box__num { width: 1.8rem; height: 1.8rem; border-radius: 50%; background: #343841; color: #fff; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 0.85rem; }
.cp-box__title { font-weight: 600; color: #2c2c2c; margin-left: 0.35rem; }
.cp-box__rename { margin: 0; flex-shrink: 0; }
.cp-box__rename .md-icon { font-size: 18px !important; color: #8a9099 !important; }
.cp-box__name-input {
  margin-left: 0.35rem; flex: 1; min-width: 0; max-width: 360px;
  border: none; border-bottom: 2px solid #343841; background: transparent;
  font-size: 1rem; font-weight: 600; color: #2c2c2c; padding: 0.2rem 0.1rem;
}
.cp-box__name-input:focus { outline: none; }
.cp-box__name-input--err { border-bottom-color: #d9534f; }
.cp-box__name-err { color: #d9534f; font-size: 0.75rem; font-weight: 600; margin-left: 0.4rem; white-space: nowrap; }
.cp-box__actions { margin-left: auto; display: flex; }
.cp-box__delete .md-icon { color: #d9534f; }

.cp-empty { font-style: italic; color: #999; font-size: 0.85rem; margin: 0.25rem 0 0.75rem; }

/* Elements stacked, centered; the list scrolls inside the fixed-height box.
   Recessed background + inset shadow give depth so it reads as a scroll area. */
.cp-el-list {
  flex: 1; min-height: 0; overflow-y: auto;
  display: flex; flex-direction: column; align-items: center; gap: 0.75rem;
  padding: 0.85rem;
  background: #f4f6f8;
  border: 1px solid #e6e9ed;
  border-radius: 10px;
  box-shadow: inset 0 6px 8px -6px rgba(0,0,0,0.18), inset 0 -6px 8px -6px rgba(0,0,0,0.18);
}
/* Slim, visible scrollbar to reinforce that the area scrolls. */
.cp-el-list::-webkit-scrollbar { width: 8px; }
.cp-el-list::-webkit-scrollbar-thumb { background: #c2c8cf; border-radius: 4px; }
.cp-el-list::-webkit-scrollbar-track { background: transparent; }
.cp-el { width: 100%; max-width: 820px; border: 1px solid #e8e8e8; border-radius: 10px; padding: 0.6rem 0.85rem 0.85rem; background: #fff; box-sizing: border-box; box-shadow: 0 1px 3px rgba(0,0,0,0.06); flex-shrink: 0; }
.cp-el__bar { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
.cp-el__icon { color: #6b7280 !important; font-size: 18px !important; }
.cp-el__type { font-size: 0.78rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.03em; }
.cp-el__main { margin: 0; }
.cp-el__main .md-icon { font-size: 19px !important; color: #b9bec6 !important; }
.cp-el__main--on .md-icon { color: #f2b705 !important; }
.cp-el__actions { margin-left: auto; display: flex; }
.cp-el__delete .md-icon { color: #d9534f; }

/* Footer row holding the round + button, right-aligned under the scroll area. */
.cp-box__footer { display: flex; justify-content: flex-end; flex-shrink: 0; margin-top: 0.75rem; }
.cp-box__fab {
  width: 44px; min-width: 44px; height: 44px; margin: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: #343841 !important;
  box-shadow: 0 2px 8px rgba(52,56,65,0.35);
}
.cp-box__fab:hover { background: #2a2d34 !important; }
.cp-box__fab >>> .md-button-content { display: inline-flex; align-items: center; justify-content: center; }
.cp-box__fab .md-icon { color: #fff !important; margin: 0 !important; font-size: 22px !important; }
</style>
